import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import FiltersBar from "../components/FiltersBar";
import GalleryListing from "../components/GalleryListing";
import GalleryPagination from "../components/GalleryPagination";
import {ArtisticObjectListing} from "../../admin/artisticobjects/types/ArtisticObject";
import {handleError} from "../../common/utils/errorHandling";
import GalleryHeader from "../components/GalleryHeader";
import searchService from "../services/searchService";
import {useSearch} from "../../common/contexts/SearchContext";
import preferencesService from "../../common/services/preferencesService";

const Gallery: React.FC = () => {
  const {t} = useTranslation();
  const [artisticObjects, setArtisticObjects] = useState<ArtisticObjectListing[]>([]);
  const { searchCriteria, updateSearchCriteria } = useSearch();
  const [pageIndex, setPageIndex] = useState(0);

  const [zoomedId, setZoomedIn] = useState(() => preferencesService.getGalleryDisplay().zoomedIn);
  const [withDescriptions, setWithDescriptions] = useState(() => preferencesService.getGalleryDisplay().withDescriptions);
  const [columnsCount, setWithColumnsCount] = useState(() => preferencesService.getGalleryDisplay().columnsCount);
  const [itemsPerPage, setItemsPerPage] = useState(() => preferencesService.getGalleryPagination().itemsPerPage);

  useEffect(() => {
    searchArtisticObjects();
  }, [searchCriteria]);

  useEffect(() => {
    preferencesService.updateGalleryDisplay({ zoomedIn: zoomedId });
  }, [zoomedId]);

  useEffect(() => {
    preferencesService.updateGalleryDisplay({ withDescriptions });
  }, [withDescriptions]);

  useEffect(() => {
    preferencesService.updateGalleryDisplay({ columnsCount });
  }, [columnsCount]);

  useEffect(() => {
    preferencesService.updateGalleryPagination({ itemsPerPage });
  }, [itemsPerPage]);

  const searchArtisticObjects = async (): Promise<void> => {
    await handleError(
      async () => {
        const objectsData = await searchService.searchArtisticObjects(searchCriteria);
        setArtisticObjects(objectsData);
        setPageIndex(0); // Reset to first page when search criteria change
      },
      'Error searching artistic objects'
    );
  };

  const handleItemsPerPageChange = (newItemsPerPage: number): void => {
    setItemsPerPage(newItemsPerPage);
    setPageIndex(0); // Reset to first page when changing items per page
  };

  const totalCount = artisticObjects.length;
  const startIndex = pageIndex * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const displayedObjects = artisticObjects.slice(startIndex, endIndex);

  const currentRangeStart = totalCount > 0 ? startIndex + 1 : 0;
  const currentRangeEnd = Math.min(endIndex, totalCount);

  return (
    <>
      <FiltersBar
        searchCriteria={searchCriteria}
        onSearchCriteriaChange={updateSearchCriteria}
        disabledFilters={['publications']}
      />
      <GalleryHeader
        title={t('gallery.header.title')}
        introduction={t('gallery.header.introduction')}
        range={`${currentRangeStart}-${currentRangeEnd}`}
        totalCount={totalCount}
        zoomedIn={zoomedId}
        withDescriptions={withDescriptions}
        columnsCount={columnsCount}
        onToggleZoom={zoomedId => setZoomedIn(zoomedId)}
        onToggleDescriptions={withDescriptions => setWithDescriptions(withDescriptions)}
        onChangeColumns={columns => setWithColumnsCount(columns)}
      />
      <GalleryListing
        artisticObjects={displayedObjects}
        withDescriptions={withDescriptions}
        columnsCount={columnsCount}
        pagination={(
          <GalleryPagination
            pageIndex={pageIndex}
            totalCount={totalCount}
            itemsPerPage={itemsPerPage}
            onSetPageIndex={setPageIndex}
            onItemsPerPageChange={handleItemsPerPageChange}
          />
        )}
        zoomedIn={zoomedId}
      />
    </>
  );
};

export default Gallery;
